<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { api, type SwingWatchResponse } from '../../api/client'
import { usePortfolio } from '../../composables/usePortfolio'
import { fundForPortfolio, pageLabel, registryEntryForFund, type Fund } from './fundConfig'
import SwingWatchCard from './SwingWatchCard.vue'

// Spec 2026-10-17 §7 / D2: every swing-priced fund the viewer can see, page fund first
// (Paolo option B), then the API's registry order. One request for the whole section.
const data = ref<SwingWatchResponse | null>(null)
const failed = ref(false)
const { portfolio } = usePortfolio()
const host = computed(() => fundForPortfolio(portfolio.value))

// ONE label helper for card titles and the footer (pageLabel keeps "Gold" casing).
function label(fund: Fund): string {
  try { return pageLabel(registryEntryForFund(fund).slug) } catch { return fund }
}
const title = (fund: Fund) => label(fund).toUpperCase() === fund.toUpperCase() ? label(fund) : `${label(fund)} (${fund})`

const ordered = computed(() => {
  const fs = data.value?.funds ?? []
  return [...fs.filter(f => f.fund === host.value), ...fs.filter(f => f.fund !== host.value)]
})

async function load() {
  try { data.value = await api.getSwingWatch(); failed.value = false } catch { if (!data.value) failed.value = true }
}
let timer: number | null = null
onMounted(() => { void load(); timer = window.setInterval(() => { void load() }, 300_000) })
onUnmounted(() => { if (timer !== null) window.clearInterval(timer) })
</script>

<template>
  <div class="swing-section" data-testid="swing-pricing-section">
    <p class="sp-intro">Official NAV vs our noon estimate, last 60 dealing days, per fund. Dashed = ±2 sd of that fund's own gaps.</p>
    <p v-if="failed" class="sp-error">Swing pricing couldn't load — it retries every 5 minutes.</p>
    <SwingWatchCard v-for="f in ordered" :key="f.fund" :view="f" :title="title(f.fund)" />
    <p v-if="data?.not_swing_priced.length" class="sp-foot" data-testid="swing-not-priced">
      Not swing-priced:
      <span v-for="(n, i) in data.not_swing_priced" :key="n.fund">{{ i ? ' · ' : '' }}{{ label(n.fund) }}<template v-if="n.note"> ({{ n.note }})</template></span>
    </p>
  </div>
</template>

<style scoped>
.swing-section { display: flex; flex-direction: column; gap: 0.75rem; min-width: 0; }
.sp-intro, .sp-foot { opacity: .7; font-size: 11px; margin: 4px 0; overflow-wrap: anywhere; }
.sp-error { color: #e5a83b; font-size: 12px; }
</style>
